import type {
  CompanyLookupApiData,
  CompanyLookupApiResponses,
  DepartmentAddressesDeleteApiData,
  DepartmentAddressesDeleteApiResponses,
  DepartmentCarsAddApiData,
  DepartmentCarsAddApiResponses,
  DepartmentCreateApiData,
  DepartmentCreateApiResponses,
  DepartmentDeleteApiData,
  DepartmentDeleteApiResponses,
  DepartmentGetApiData,
  DepartmentGetApiResponses,
  DepartmentGetBySlugApiData,
  DepartmentGetBySlugApiResponses,
  DepartmentListApiData,
  DepartmentListApiResponses,
  DepartmentMemberBulkCreateApiData,
  DepartmentMemberBulkCreateApiResponses,
  DepartmentMemberCreateApiData,
  DepartmentMemberCreateApiResponses,
  DepartmentMemberDeleteApiData,
  DepartmentMemberDeleteApiResponses,
  DepartmentMemberGetApiData,
  DepartmentMemberGetApiResponses,
  DepartmentMemberListApiData,
  DepartmentMemberListApiResponses,
  DepartmentMemberPatchApiData,
  DepartmentMemberPatchApiResponses,
  DepartmentPatchApiData,
  DepartmentPatchApiResponses,
  ExistingUserOrganizationSignupApiData,
  ExistingUserOrganizationSignupApiResponses,
  OrganizationCreateApiData,
  OrganizationCreateApiResponses,
  OrganizationDeleteApiData,
  OrganizationDeleteApiResponses,
  OrganizationGetApiData,
  OrganizationGetApiResponses,
  OrganizationListApiData,
  OrganizationListApiResponses,
  OrganizationMinimalListApiData,
  OrganizationMinimalListApiResponses,
  OrganizationPatchApiData,
  OrganizationPatchApiResponses,
  OrganizationRegistrationNumberPatchApiData,
  OrganizationRegistrationNumberPatchApiResponses,
  OrganizationSignupCompanyLookupApiData,
  OrganizationSignupCompanyLookupApiResponses,
  OrganizationSignupCompanySearchApiData,
  OrganizationSignupCompanySearchApiResponses,
  OrganizationsForCarApiData,
  OrganizationsForCarApiResponses
} from '@navio/nsdk';

import type { ApiInput } from '../../utils';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export const getOrganizationUrls = {
  getOrganizationSignupCompanyLookup: dataEndpoint<
    OrganizationSignupCompanyLookupApiData,
    OrganizationSignupCompanyLookupApiResponses
  >({
    getUrl: (queryParams) => apiUrl('organizations/signup/company-lookup/', queryParams)
  }),
  getOrganizationSignupCompanySearch: dataEndpoint<
    OrganizationSignupCompanySearchApiData,
    OrganizationSignupCompanySearchApiResponses
  >({
    getUrl: (queryParams) => apiUrl('organizations/signup/company-search/', queryParams)
  }),
  postExistingUserOrganizationSignup: responseEndpoint<
    ExistingUserOrganizationSignupApiData,
    ExistingUserOrganizationSignupApiResponses
  >({
    getUrl: () => apiUrl('organizations/signup/existing-user/'),
    getBody: (args) => args
  }),
  postOrganization: responseEndpoint<OrganizationCreateApiData, OrganizationCreateApiResponses>({
    getUrl: ({ logo: _logo, contactDetails: _contactDetails, nameLegal: _nameLegal, ...queryParams }) =>
      apiUrl('organizations/', queryParams),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    getBody: ({ logo, contactDetails, nameLegal }) => {
      const formData = new FormData();
      if (logo) {
        formData.append('logo', logo);
      }
      if (contactDetails?.email) {
        formData.append('contact_details.email', contactDetails.email);
      }
      if (contactDetails?.phoneNumber) {
        formData.append('contact_details.phone_number', contactDetails.phoneNumber);
      }
      if (contactDetails?.website) {
        formData.append('contact_details.website', contactDetails.website);
      }
      if (nameLegal) {
        formData.append('name_legal', nameLegal);
      }
      return formData;
    },
    skipBodyTransformation: true
  }),
  patchOrganization: responseEndpoint<OrganizationPatchApiData, OrganizationPatchApiResponses>({
    getUrl: ({ id }) => apiUrl(`organizations/${id}/`),
    getBody: ({ id: _id, ...patch }) => patch
  }),
  patchOrganizationRegistrationNumber: responseEndpoint<
    OrganizationRegistrationNumberPatchApiData,
    OrganizationRegistrationNumberPatchApiResponses
  >({
    getUrl: ({ id }) => apiUrl(`organizations/${id}/registration-number/`),
    getBody: ({ id: _id, ...patch }) => patch
  }),
  deleteOrganization: dataEndpoint<OrganizationDeleteApiData, OrganizationDeleteApiResponses>({
    getUrl: ({ id }) => apiUrl(`organizations/${id}/`)
  }),
  getPaginatedOrganizations: dataEndpoint<OrganizationListApiData, OrganizationListApiResponses>({
    getUrl: (queryParams) => apiUrl('organizations/', queryParams)
  }),
  getPaginatedMinimalOrganizations: dataEndpoint<OrganizationMinimalListApiData, OrganizationMinimalListApiResponses>({
    getUrl: (queryParams) => apiUrl('organizations/minimal/', queryParams)
  }),
  getOrganization: dataEndpoint<OrganizationGetApiData, OrganizationGetApiResponses>({
    getUrl: ({ id }) => apiUrl(`organizations/${id}/`)
  }),
  patchOrganizationImage: responseEndpoint<OrganizationPatchApiData, OrganizationPatchApiResponses>({
    getUrl: ({ id }) => apiUrl(`organizations/${id}/`),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    getBody: (data) => data,
    skipBodyTransformation: true
  }),
  getPaginatedDepartments: dataEndpoint<DepartmentListApiData, DepartmentListApiResponses>({
    getUrl: (queryParams) => apiUrl('departments/', queryParams)
  }),
  getDepartment: dataEndpoint<DepartmentGetApiData, DepartmentGetApiResponses>({
    getUrl: ({ departmentId }) => apiUrl(`departments/${departmentId}/`)
  }),
  getDepartmentBySlug: dataEndpoint<DepartmentGetBySlugApiData, DepartmentGetBySlugApiResponses>({
    getUrl: ({ slug }) => apiUrl(`departments/by-slug/${slug}/`)
  }),
  postDepartment: responseEndpoint<DepartmentCreateApiData, DepartmentCreateApiResponses>({
    getUrl: () => apiUrl('departments/'),
    getBody: (data) => data
  }),
  patchDepartment: responseEndpoint<DepartmentPatchApiData, DepartmentPatchApiResponses>({
    getUrl: ({ departmentId }) => apiUrl(`departments/${departmentId}/`),
    getBody: ({ departmentId: _departmentId, ...patch }) => patch
  }),
  deleteDepartment: dataEndpoint<DepartmentDeleteApiData, DepartmentDeleteApiResponses>({
    getUrl: ({ departmentId }) => apiUrl(`departments/${departmentId}/`)
  }),
  postDeleteDepartmentAddresses: responseEndpoint<
    DepartmentAddressesDeleteApiData,
    DepartmentAddressesDeleteApiResponses
  >({
    getUrl: ({ departmentId }) => apiUrl(`departments/${departmentId}/delete-addresses/`),
    getBody: ({ departmentId: _, ...body }) => body
  }),
  postAddCarsToDepartment: responseEndpoint<DepartmentCarsAddApiData, DepartmentCarsAddApiResponses>({
    getUrl: ({ departmentId }) => apiUrl(`departments/${departmentId}/add-cars/`),
    getBody: ({ departmentId: _, ...body }) => body
  }),
  patchDepartmentImage: responseEndpoint<
    DepartmentPatchApiData,
    DepartmentPatchApiResponses,
    ApiInput<DepartmentPatchApiData> & { logo: File | Blob | null }
  >({
    getUrl: ({ departmentId }) => apiUrl(`departments/${departmentId}/`),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    getBody: (data) => data,
    skipBodyTransformation: true
  }),
  getPaginatedDepartmentMembers: dataEndpoint<DepartmentMemberListApiData, DepartmentMemberListApiResponses>({
    getUrl: (queryParams) => apiUrl('department-members/', queryParams)
  }),
  getDepartmentMember: dataEndpoint<DepartmentMemberGetApiData, DepartmentMemberGetApiResponses>({
    getUrl: ({ userGroupMemberId }) => apiUrl(`department-members/${userGroupMemberId}/`)
  }),
  postDepartmentMember: responseEndpoint<DepartmentMemberCreateApiData, DepartmentMemberCreateApiResponses>({
    getUrl: () => apiUrl('department-members/'),
    getBody: (data) => data
  }),
  postDepartmentMembersBulk: responseEndpoint<
    DepartmentMemberBulkCreateApiData,
    DepartmentMemberBulkCreateApiResponses
  >({
    getUrl: () => apiUrl('department-members/bulk-create/'),
    getBody: (data) => data
  }),
  patchDepartmentMember: responseEndpoint<DepartmentMemberPatchApiData, DepartmentMemberPatchApiResponses>({
    getUrl: ({ userGroupMemberId }) => apiUrl(`department-members/${userGroupMemberId}/`),
    getBody: ({ userGroupMemberId: _, ...body }) => body
  }),
  deleteDepartmentMember: dataEndpoint<DepartmentMemberDeleteApiData, DepartmentMemberDeleteApiResponses>({
    getUrl: ({ userGroupMemberId }) => apiUrl(`department-members/${userGroupMemberId}/`)
  }),
  getOrganizationsForCar: dataEndpoint<OrganizationsForCarApiData, OrganizationsForCarApiResponses>({
    getUrl: (queryParams) => apiUrl('organizations/for-car/', queryParams)
  }),
  getCompanyLookup: dataEndpoint<CompanyLookupApiData, CompanyLookupApiResponses>({
    getUrl: (queryParams) => apiUrl('company-lookup/', queryParams),
    sideEffectGet: true
  })
};
